import { logger } from '@ab-core/logger';

declare global {
    interface Window {
        __googleTranslateFixApplied?: boolean;
    }
}

type DOMErrorLogInfos = {
    error: DOMException;
    parentNodeName: string;
    childNodeName: string;
    url: string;
    userAgent: string;
    stackTrace: string | undefined;
    translationActive: boolean;
};

/** Detects if browser translation is active. */
const isTranslationActive = (): boolean => {
    try {
        const html = document.documentElement;

        return (
            html.classList.contains('translated-ltr') ||
            html.classList.contains('translated-rtl') ||
            html.hasAttribute('translate') ||
            document.querySelector('html.translated') !== null ||
            document.body?.querySelector('font.notranslate, font[style]') !== null
        );
    } catch {
        // DOM may be unstable during error
        return false;
    }
};

/**
 * Patches Node.prototype.removeChild/insertBefore to handle NotFoundError.
 * @see https://github.com/facebook/react/issues/11538
 */
export const applyMachineTranslateFix = (): void => {
    if (typeof window === 'undefined' || window.__googleTranslateFixApplied) {
        return;
    }

    const originalRemoveChild = Node.prototype.removeChild;

    // NOSONAR - Intentional prototype monkey-patching Translate workaround
    Node.prototype.removeChild = function <T extends Node>(child: T): T {
        // NOSONAR - Intentional prototype monkey-patching
        try {
            return originalRemoveChild.call(this, child) as T;
        } catch (error) {
            if (error instanceof DOMException && error.name === 'NotFoundError') {
                const translationActive = isTranslationActive();

                logger.info<DOMErrorLogInfos>({
                    title: 'RemoveChild NotFoundError intercepted',
                    infos: {
                        parentNodeName: this.nodeName || 'unknown',
                        childNodeName: (child as Node)?.nodeName || 'unknown',
                        url: typeof location !== 'undefined' ? location.href : 'unknown',
                        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
                        stackTrace: new Error().stack,
                        error,
                        translationActive
                    }
                });

                return child;
            }

            throw error;
        }
    };

    const originalInsertBefore = Node.prototype.insertBefore;

    // NOSONAR - Intentional prototype monkey-patching Translate workaround
    Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
        // NOSONAR - Intentional prototype monkey-patching
        try {
            return originalInsertBefore.call(this, newNode, referenceNode) as T;
        } catch (error) {
            if (error instanceof DOMException && error.name === 'NotFoundError') {
                const translationActive = isTranslationActive();

                logger.info<DOMErrorLogInfos>({
                    title: 'InsertBefore NotFoundError intercepted',
                    infos: {
                        parentNodeName: this.nodeName || 'unknown',
                        childNodeName: (newNode as Node)?.nodeName || 'unknown',
                        url: typeof location !== 'undefined' ? location.href : 'unknown',
                        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
                        stackTrace: new Error().stack,
                        error,
                        translationActive
                    }
                });

                return this.appendChild(newNode);
            }

            throw error;
        }
    };
    window.__googleTranslateFixApplied = true;
};
